import React, { useEffect } from 'react';
import { preloadResource, prefetchPage, addResourceHints } from '@/utils/performance';

import { log } from "@/utils/log";

interface CriticalResource {
  href: string;
  as: string;
  type?: string;
  priority: 'high' | 'medium' | 'low';
  crossOrigin?: string;
}

interface CriticalResourcePreloaderProps {
  resources?: CriticalResource[];
  prefetchRoutes?: string[];
  enableDNSPrefetch?: boolean;
  enablePreconnect?: boolean;
}

const defaultCriticalResources: CriticalResource[] = [
  // Critical fonts
  {
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
    as: 'style',
    priority: 'high',
    crossOrigin: 'anonymous'
  },
  // Hero images for above-the-fold content
  {
    href: '/lovable-uploads/2f6c15f0-0e51-4e82-83ca-b7895b7e3f3e.png',
    as: 'image',
    priority: 'high'
  },
  // Critical icons
  {
    href: '/icon-192.png',
    as: 'image',
    priority: 'medium'
  },
  {
    href: '/icon-512.png',
    as: 'image',
    priority: 'medium'
  }
];

const defaultPrefetchRoutes = [
  '/feed',
  '/disney-world-tips',
  '/disney-photography',
  '/disney-trip-planning',
  '/disney-attractions',
  '/disney-restaurants'
];

export const CriticalResourcePreloader: React.FC<CriticalResourcePreloaderProps> = ({
  resources = defaultCriticalResources,
  prefetchRoutes = defaultPrefetchRoutes,
  enableDNSPrefetch = true,
  enablePreconnect = true
}) => {
  useEffect(() => {
    // Preload critical resources immediately
    const preloadCriticalResources = () => {
      // Sort by priority and preload
      const sortedResources = resources.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      sortedResources.forEach((resource, index) => {
        // Stagger preloading to avoid overwhelming the browser
        setTimeout(() => {
          try {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = resource.href;
            link.as = resource.as;
            
            if (resource.type) {
              link.type = resource.type;
            }
            
            if (resource.crossOrigin) {
              link.crossOrigin = resource.crossOrigin;
            }

            // Add importance hint for high priority resources
            if (resource.priority === 'high') {
              link.setAttribute('importance', 'high');
            }

            document.head.appendChild(link);
            
            log.log(`Preloaded: ${resource.href} (${resource.priority} priority)`);
          } catch (error) {
            log.warn(`Failed to preload resource: ${resource.href}`, error);
          }
        }, index * 100); // Stagger by 100ms
      });
    };

    // Prefetch likely next pages
    const prefetchLikelyPages = () => {
      // Wait a bit before prefetching to not interfere with critical resources
      setTimeout(() => {
        prefetchRoutes.forEach((route, index) => {
          setTimeout(() => {
            try {
              prefetchPage(route);
              log.log(`Prefetched route: ${route}`);
            } catch (error) {
              log.warn(`Failed to prefetch route: ${route}`, error);
            }
          }, index * 200); // Stagger prefetching
        });
      }, 2000);
    };

    // Add DNS prefetch and preconnect hints
    const addPerformanceHints = () => {
      if (enableDNSPrefetch || enablePreconnect) {
        try {
          const criticalDomains = [
            'fonts.googleapis.com',
            'fonts.gstatic.com',
            'lxlkweksijlrliiauulx.supabase.co',
            'images.unsplash.com'
          ];

          criticalDomains.forEach(domain => {
            if (enableDNSPrefetch) {
              const dnsPrefetch = document.createElement('link');
              dnsPrefetch.rel = 'dns-prefetch';
              dnsPrefetch.href = `//${domain}`;
              document.head.appendChild(dnsPrefetch);
            }

            if (enablePreconnect) {
              const preconnect = document.createElement('link');
              preconnect.rel = 'preconnect';
              preconnect.href = `https://${domain}`;
              preconnect.crossOrigin = 'anonymous';
              document.head.appendChild(preconnect);
            }
          });

          log.log('Performance hints added');
        } catch (error) {
          log.warn('Failed to add performance hints:', error);
        }
      }
    };

    // Preload critical CSS for above-the-fold content
    const preloadCriticalCSS = () => {
      const criticalCSS = `
        /* Critical above-the-fold styles */
        .hero-section { opacity: 1; }
        .navigation { opacity: 1; }
        .hero-gradient { background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-foreground))); }
      `;

      const style = document.createElement('style');
      style.textContent = criticalCSS;
      style.setAttribute('data-critical', 'true');
      document.head.appendChild(style);
    };

    // Execute preloading strategies
    preloadCriticalResources();
    addPerformanceHints();
    preloadCriticalCSS();
    
    // Prefetch pages after initial load
    if (document.readyState === 'complete') {
      prefetchLikelyPages();
    } else {
      window.addEventListener('load', prefetchLikelyPages);
    }

    // Cleanup
    return () => {
      window.removeEventListener('load', prefetchLikelyPages);
    };
  }, [resources, prefetchRoutes, enableDNSPrefetch, enablePreconnect]);

  // This component doesn't render anything
  return null;
};